// apps/admin/src/routes/System.tsx
/**
 * System Status Page
 * Full system health overview for admins
 */

import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Progress,
  Alert,
  AlertTitle,
  AlertDescription,
} from "@jourfx/ui";
import {
  Activity,
  Server,
  Database,
  HardDrive,
  Users,
  Video,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { api } from "@jourfx/convex/client";
import { PageHeader } from "../components/PageHeader";
import { SystemSettingsDialog } from "../components/SystemSettingsDialog";
import { getBuildInfo } from "../lib/buildInfo";

type ServiceStatus = "operational" | "degraded" | "down";

interface ServiceInfo {
  id: string;
  name: string;
  status: ServiceStatus;
  responseTime?: number;
  lastChecked: number | null;
  errorMessage?: string;
}

interface ServiceCardProps {
  name: string;
  status: ServiceStatus;
  responseTime?: number;
  errorMessage?: string;
  lastChecked: number | null;
  showDetails: boolean;
}

function ServiceCard({
  name,
  status,
  responseTime,
  errorMessage,
  lastChecked,
  showDetails,
}: ServiceCardProps) {
  const { t } = useTranslation("system");

  const statusConfig: Record<
    ServiceStatus,
    { icon: typeof CheckCircle; color: string; bg: string; label: string }
  > = {
    operational: {
      icon: CheckCircle,
      color: "text-success",
      bg: "bg-success-muted",
      label: t("status.operational", "Operational"),
    },
    degraded: {
      icon: AlertTriangle,
      color: "text-warning",
      bg: "bg-warning-muted",
      label: t("status.degraded", "Degraded"),
    },
    down: {
      icon: XCircle,
      color: "text-error",
      bg: "bg-error-muted",
      label: t("status.down", "Down"),
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Card className={`${config.bg} border-0`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Icon className={`h-6 w-6 ${config.color}`} />
            <div>
              <p className="font-medium text-foreground">{name}</p>
              <p className={`text-sm ${config.color}`}>{config.label}</p>
            </div>
          </div>
          {showDetails && responseTime !== undefined && (
            <span className="text-sm text-muted-foreground tabular-nums">
              {responseTime}ms
            </span>
          )}
        </div>
        {showDetails && errorMessage && (
          <p className="mt-2 text-sm text-error">{errorMessage}</p>
        )}
        {showDetails && lastChecked && (
          <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
            <RefreshCw className="h-3 w-3" />
            {t("lastChecked")}: {formatTimeAgo(lastChecked, t)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function formatTimeAgo(timestamp: number, t?: (key: string, opts?: Record<string, unknown>) => string): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (t) {
    if (seconds < 60) return t("timeAgo.seconds", { count: seconds });
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t("timeAgo.minutes", { count: minutes });
    const hours = Math.floor(minutes / 60);
    return t("timeAgo.hours", { count: hours });
  }
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

interface CapacityGaugeProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  used: number;
  limit: number;
  unit: "bytes" | "count";
}

function CapacityGauge({ icon: Icon, label, used, limit, unit }: CapacityGaugeProps) {
  const percentage = Math.min((used / limit) * 100, 100);
  const isWarning = percentage > 80;
  const isCritical = percentage > 90;

  const formatValue = (val: number) =>
    unit === "bytes" ? formatBytes(val) : val.toLocaleString();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className="text-sm text-muted-foreground">
          {formatValue(used)} / {formatValue(limit)}
        </span>
      </div>
        <Progress
        value={percentage}
        className={`h-2 ${
          isCritical
            ? "[&>div]:bg-error"
            : isWarning
              ? "[&>div]:bg-warning"
              : "[&>div]:bg-success"
        }`}
      />
      <p className="text-xs text-muted-foreground text-right">
        {percentage.toFixed(0)}%
      </p>
    </div>
  );
}

export function System() {
  const { t } = useTranslation("system");
  const { t: tc } = useTranslation("common");
  const buildInfo = getBuildInfo();
  const status = useQuery(api.admin.systemHealth.getStatus);
  const metrics = useQuery(api.admin.systemHealth.getMetrics);

  if (status === undefined) {
    return <SystemSkeleton />;
  }

  if (status === null) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>{t("error.title", "Access Denied")}</AlertTitle>
        <AlertDescription>
          {t("error.noAccess", "You do not have permission to view system status.")}
        </AlertDescription>
      </Alert>
    );
  }

  const hasIssues = status.services.some((s: ServiceInfo) => s.status !== "operational");

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title", "System Status")}
        description={t("subtitle", "Overall health of your JourFx installation")}
        actions={
          <div className="flex items-center gap-4">
            {status.isSuperAdmin && <SystemSettingsDialog />}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              {t("lastUpdated")}: {formatTimeAgo(status.lastUpdated, t)}
            </div>
          </div>
        }
      />

      {/* Alert if issues */}
      {hasIssues && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("alert.issuesDetected", "Issues Detected")}</AlertTitle>
          <AlertDescription>
            {t(
              "alert.someServicesDown",
              "Some services are experiencing issues. Check the details below."
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Services Grid */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Server className="h-5 w-5" />
          {t("sections.services", "Services")}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {status.services.map((service: ServiceInfo) => (
            <ServiceCard
              key={service.id}
              name={service.name}
              status={service.status}
              responseTime={service.responseTime}
              errorMessage={service.errorMessage}
              lastChecked={service.lastChecked}
              showDetails={status.isSuperAdmin}
            />
          ))}
        </div>
      </div>

      {/* Activity Section */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Current Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {t("sections.currentActivity", "Current Activity")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span>{t("activity.activeUsers", "Active Users")}</span>
              </div>
              <span className="font-bold text-lg tabular-nums">
                {status.activity.activeUsers}
              </span>
            </div>
            {status.isSuperAdmin && metrics && (
              <>
                <div className="pl-6 text-sm text-muted-foreground space-y-1">
                  <p>{metrics.usersByRole.gutachter} {tc("roles.gutachter")}</p>
                  <p>{metrics.usersByRole.assistent} {tc("roles.assistent")}</p>
                </div>
              </>
            )}
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-muted-foreground" />
                <span>{t("activity.activeVideoSessions", "Active Video Sessions")}</span>
              </div>
              <span className="font-bold text-lg tabular-nums">
                {status.activity.activeVideoSessions}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{t("activity.appointmentsToday", "Appointments Today")}</span>
              </div>
              <span className="font-bold text-lg tabular-nums">
                {status.activity.appointmentsToday}
              </span>
            </div>
            {status.isSuperAdmin && metrics && (
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{t("activity.upcomingAppointments", "Upcoming (24h)")}</span>
                </div>
                <span className="font-bold text-lg tabular-nums">
                  {metrics.upcomingAppointments}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Capacity (Super-Admin only) */}
        {status.isSuperAdmin && metrics && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5" />
                {t("sections.capacity", "Capacity")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <CapacityGauge
                icon={Database}
                label={t("capacity.database", "Database")}
                used={metrics.capacity.database.used}
                limit={metrics.capacity.database.limit}
                unit="bytes"
              />
              <CapacityGauge
                icon={HardDrive}
                label={t("capacity.storage", "Storage")}
                used={metrics.capacity.storage.used}
                limit={metrics.capacity.storage.limit}
                unit="bytes"
              />
              <CapacityGauge
                icon={Users}
                label={t("capacity.users", "Licensed Users")}
                used={metrics.capacity.users.used}
                limit={metrics.capacity.users.limit}
                unit="count"
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* System Info (Super-Admin only) */}
      {status.isSuperAdmin && metrics && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              {t("sections.systemInfo", "System Info")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-sm">
              <div>
                <p className="text-muted-foreground">{t("info.adminVersion", "Admin Version")}</p>
                <p className="font-medium">{buildInfo.appVersion}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t("info.convex", "Convex")}</p>
                <p className="font-medium">{metrics.systemInfo.convexVersion}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t("info.buildId", "Build ID")}</p>
                <p className="font-medium break-all">{buildInfo.buildId}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t("info.builtAt", "Built at")}</p>
                <p className="font-medium">{buildInfo.builtAt}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SystemSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Skeleton className="h-64 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    </div>
  );
}
